import { useEffect, useState } from 'react';
import { collection, onSnapshot, orderBy, query, addDoc, updateDoc, doc, arrayUnion } from 'firebase/firestore';
import { db } from '../firebase/config';
import { useAuth } from '../contexts/AuthContext';

export interface FreeVideo {
  id?: string;
  title: string;
  description?: string;
  topic?: string;
  youtubeUrl: string;
  tags?: string[];
  createdAt: string;
  updatedAt: string;
  comments?: FreeVideoComment[];
}

export interface FreeVideoComment {
  authorId: string;
  authorName: string;
  content: string;
  createdAt: string;
}

export const useFreeVideos = () => {
  const [videos, setVideos] = useState<FreeVideo[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { currentUser } = useAuth();

  useEffect(() => {
    setLoading(true);
    const q = query(collection(db, 'free_videos'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list: FreeVideo[] = [];
        snap.forEach((d) => {
          list.push({ id: d.id, ...(d.data() as any) });
        });
        setVideos(list);
        setLoading(false);
      },
      (err) => {
        console.error('Failed to load free videos', err);
        setError('Failed to load free videos');
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const addVideo = async (video: Omit<FreeVideo, 'id' | 'createdAt' | 'updatedAt' | 'comments'>) => {
    const normalizedRole = (currentUser?.role || '').toString().trim().toLowerCase();
    if (!currentUser || normalizedRole !== 'admin') {
      throw new Error('Only admins can add free resources');
    }

    // Basic validation
    const title = (video.title || '').trim();
    const youtubeUrl = (video.youtubeUrl || '').trim();
    if (!title) throw new Error('Title is required');
    if (!youtubeUrl) throw new Error('YouTube URL is required');

    const now = new Date().toISOString();
    // Build payload without undefined fields (Firestore rejects undefined)
    const payload: any = {
      title,
      youtubeUrl,
      createdAt: now,
      updatedAt: now,
      comments: [],
    };
    if (video.topic !== undefined && video.topic !== '') payload.topic = video.topic;
    if (video.description !== undefined && video.description !== '') payload.description = video.description;
    if (Array.isArray(video.tags) && video.tags.length > 0) payload.tags = video.tags;

    await addDoc(collection(db, 'free_videos'), payload);
  };

  const addComment = async (videoId: string, content: string) => {
    if (!currentUser) throw new Error('User not authenticated');
    const videoRef = doc(db, 'free_videos', videoId);
    const newComment: FreeVideoComment = {
      authorId: currentUser.uid,
      authorName: currentUser.name || currentUser.email || 'Student',
      content,
      createdAt: new Date().toISOString(),
    };
    await updateDoc(videoRef, {
      comments: arrayUnion(newComment),
      updatedAt: new Date().toISOString(),
    });
  };

  const getTopics = () => {
    const topics = new Set<string>();
    videos.forEach((v) => { if (v.topic) topics.add(v.topic); });
    return Array.from(topics).sort();
  };

  return { videos, loading, error, addVideo, addComment, getTopics };
};


